import type { Locale } from "@/lib/i18n";

/**
 * The homepage's number and date rules in one place (site review 2026-09-04):
 * a value is shown as the integer the API sent plus its suffix, a cohort date
 * is shown only while it is still ahead, and the hero's "next cohort" is the
 * earliest one across the featured courses. Nothing here invents a value.
 */

export function formatProofValue(value: number, suffix: "" | "+", locale: Locale): string {
  const n = Math.max(0, Math.floor(value));
  const formatted = new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en-IN").format(n);
  return `${formatted}${suffix}`;
}

export function isFutureCohort(iso: string | null | undefined, now: Date = new Date()): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && t > now.getTime();
}

export function formatCohortDate(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(iso));
}

/** The earliest future cohort start among the given courses, or null. */
export function earliestCohort(
  courses: ReadonlyArray<{ nextCohortStartAt?: string | null }>,
  now: Date = new Date(),
): string | null {
  let best: string | null = null;
  for (const course of courses) {
    const iso = course.nextCohortStartAt;
    if (!isFutureCohort(iso, now)) continue;
    if (best === null || new Date(iso!).getTime() < new Date(best).getTime()) best = iso!;
  }
  return best;
}

/** Hours from a course's `totalDurationMinutes`, whole hours only, null when there is nothing to say. */
export function wholeHours(totalMinutes: number | null | undefined): number | null {
  if (totalMinutes == null || !Number.isFinite(totalMinutes) || totalMinutes < 60) return null;
  return Math.floor(totalMinutes / 60);
}

/**
 * An eyebrow's letter case and spacing: uppercase and tracked in English, plain
 * in Bangla, where tracking pulls a conjunct apart and uppercasing only shouts
 * the English words inside a Bangla line (`SITE-HOME02` part 3).
 */
export function eyebrowCase(locale: Locale): string {
  return locale === "bn" ? "text-sm tracking-normal" : "text-xs uppercase tracking-[0.14em]";
}

/**
 * `SITE-HOME02` part 3: a homepage band below the first screen skips layout
 * and paint until the visitor scrolls near it (measured: the bottom half's
 * layout before the first paint, 900 to 1,340 ms, without this). One class for
 * every page since `SITE-CRS02`, in `components/showcase/below-fold.ts`.
 */
export { BELOW_FOLD_BAND } from "@/components/showcase/below-fold";
